import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Extension, isNodeEmpty } from '@tiptap/core';
import katex from 'katex';
import { extractTextAndKatexFromHtml, isHTML } from '@amit/tiptap';

export default Extension.create({
  name: 'html-katex-placeholder',

  addOptions() {
    return {
      placeholder: '',
      showOnlyWhenEditable: true,
      showOnlyCurrent: false,
      emptyNodeClass: 'is-empty',
      emptyEditorClass: 'is-editor-empty',
      includeChildren: false,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('html-katex-placeholder'),

        props: {
          decorations: ({ doc, selection }) => {
            const active =
              this.editor.isEditable || !this.options.showOnlyWhenEditable;
            const decorations: Decoration[] = [];

            const isEmptyEditor =
              doc.childCount === 1 &&
              doc.firstChild?.isTextblock &&
              doc.firstChild?.content.size === 0;

            if (!active || !isEmptyEditor) {
              return null;
            }
            const isHtml = isHTML(this.options.placeholder);
            if (isHtml) {
              const wrapper = document.createElement('div');
              wrapper.className = 'tiptap-placeholder';
              wrapper.innerHTML = this.options.placeholder;
              const elements = wrapper.querySelectorAll('[data-katex]');

              if (elements.length) {
                elements.forEach(el => {
                  const raw = el.textContent || '';
                  const latex = raw.replace(/^\$/, '').replace(/\$$/, ''); // remove $ from both sides

                  try {
                    katex.render(latex, el, {
                      throwOnError: false,
                    });
                  } catch (e) {
                    //@ts-ignore
                    el.innerText = raw;
                  }
                });

                // Place the placeholder widget at the start of the doc
                decorations.push(
                  Decoration.widget(selection.from, () => wrapper, {
                    side: -1,
                  }),
                );
                return DecorationSet.create(doc, decorations);
              }
            }

            doc.descendants((node, pos) => {
              const hasAnchor =
                selection.anchor >= pos &&
                selection.anchor <= pos + node.nodeSize;
              const isEmpty = !node.isLeaf && isNodeEmpty(node);

              if ((hasAnchor || !this.options.showOnlyCurrent) && isEmpty) {
                const classes = [this.options.emptyNodeClass];
                if (isEmptyEditor) {
                  classes.push(this.options.emptyEditorClass);
                }

                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    class: classes.join(' '),
                    'data-placeholder':
                      typeof this.options.placeholder === 'function'
                        ? this.options.placeholder({
                            editor: this.editor,
                            node,
                            pos,
                            hasAnchor,
                          })
                        : isHtml
                          ? extractTextAndKatexFromHtml(
                              this.options.placeholder,
                            )
                          : this.options.placeholder,
                  }),
                );
              }

              return this.options.includeChildren;
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});
